import {
  createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode,
} from 'react'

type Header = {
  /** Title shown in the TopBar. Pass empty string to clear. */
  title?: string
  /** Optional sub-line (subtitle / description). */
  subtitle?: string
  /** Optional right-aligned action slot (buttons, tabs, etc.). */
  /**
   * REMOVED — a page's action buttons do not go in the top bar.
   *
   * The top bar is app chrome: the sidebar trigger, the page title, the
   * notifications bell and the app switcher. A control that acts on THIS
   * page's content sat a long way from the content it acts on, moved as the
   * chrome moved, and competed with the app-level controls beside it. Offers
   * and Coupons were the only two pages using it; both now carry their action
   * in their own toolbar, next to the search and filter it belongs with.
   *
   * The field is gone rather than merely unused so the next page cannot
   * quietly re-introduce it.
   */
  /**
   * Full-bar override. When set, the TopBar renders ONLY this node inside
   * its h-14 strip (the default sidebar trigger / title / actions layout
   * is skipped). Use this for page-specific bars like a playlist info bar
   * that needs total control over the topbar contents.
   */
  render?: ReactNode
}

// Two contexts intentionally: the VALUE context drives the TopBar render
// and is the only thing that should re-render consumers when the header
// changes; the SETTER context exposes a stable updater so usePageHeader
// can write without subscribing — otherwise every setHeader would trigger
// a re-render in the page that called usePageHeader, which would build
// fresh render/actions ReactNodes with new identity, refire the effect,
// and recurse → React error #185 ("Maximum update depth exceeded").
const PageHeaderValueContext  = createContext<Header>({})
const PageHeaderSetterContext = createContext<((next: Header) => void) | null>(null)

export function PageHeaderProvider({ children }: { children: ReactNode }) {
  const [header, setHeaderState] = useState<Header>({})

  // Stable setter — created once per provider mount. usePageHeader subscribes
  // to this (which never changes) so it doesn't re-render when `header` does.
  const setHeader = useCallback((next: Header) => setHeaderState(next), [])

  // Base tab title = the per-app <title> from index.html (e.g. "WeTook Frame"),
  // captured once at mount before any page overwrites document.title.
  const appName = useRef(typeof document !== 'undefined' ? document.title : 'WeTook')

  // Best-practice tab titles: mirror the same page title the TopBar renders into
  // document.title as "<page> · <App>" (or just the app name on title-less pages).
  // One place, so every route gets a descriptive, localized tab title for free.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const base = appName.current || 'WeTook'
    document.title = header.title ? `${header.title} · ${base}` : base
  }, [header.title])

  return (
    <PageHeaderSetterContext.Provider value={setHeader}>
      <PageHeaderValueContext.Provider value={header}>
        {children}
      </PageHeaderValueContext.Provider>
    </PageHeaderSetterContext.Provider>
  )
}

/** Reads the current page header (for TopBar / shell consumers). */
export function usePageHeaderValue(): Header {
  return useContext(PageHeaderValueContext)
}

/**
 * Page-level hook: set the TopBar title + optional subtitle/actions for
 * the lifetime of the component. Clears on unmount so route changes don't
 * leak the previous page's title.
 *
 * Safe to call from a parent that re-renders frequently. Two effects:
 *   1. Push on every render (no deps). setHeader is stable so this does
 *      NOT cascade — usePageHeader doesn't subscribe to header value, only
 *      to the setter context which never changes identity. The value
 *      context's useState bails on identity match anyway, so passing the
 *      same shape twice is a no-op for TopBar.
 *   2. Clear on unmount (only). Keeps route changes from leaking the
 *      previous page's title into the next page's initial render.
 */
export function usePageHeader(header: Header) {
  const setHeader = useContext(PageHeaderSetterContext)

  // Keep the latest header in a ref so the push effect always has the
  // current value even if it fires before React re-runs the closure.
  const headerRef = useRef(header)
  headerRef.current = header

  // 1. Push on every render of the caller.
  useEffect(() => {
    if (setHeader) setHeader(headerRef.current)
  })

  // 2. Clear on unmount only — depends solely on the stable setter so
  // it never re-runs mid-mount.
  useEffect(() => {
    return () => { if (setHeader) setHeader({}) }
  }, [setHeader])
}
